import {
  ChangeDetectionStrategy,
  Component,
  inject,
  Injectable,
  ViewEncapsulation,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  EntitiesStore,
  EntityStore,
  GetEntityPipe,
  JsonApiQuery,
  LIST_SCROLL_ENTITIES_STORE,
} from '@madeinlune/massaud-json-api-bridge';
import {
  NodePartner,
  UiGridItem,
  UiProjectGridItem,
} from '@madeinlune/massaud-types';
import { LetDirective, PushPipe } from '@ngrx/component';
import { delay, map, Observable, tap } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { ComponentStore } from '@ngrx/component-store';
import {
  partnerProjectList,
  partnersDetailQuery,
  partnersListQuery,
} from '@madeinlune/massaud-query-builder';
import { MediaImageComponent } from '@nx-massaud/media/ui/image';
import { TranslocoModule } from '@ngneat/transloco';
import { RoutedComponent, RoutedComponentError } from '@nx-massaud/ui';
import { LoadingDirective } from '@nx-massaud/loading';
import { GridListComponent } from '@nx-massaud/ui/grid';

@Injectable()
class PartnersStore extends EntitiesStore<UiGridItem> {}

@Component({
  selector: 'msd-det-deat-partner',
  standalone: true,
  imports: [
    CommonModule,
    LetDirective,
    GridListComponent,
    PushPipe,
    MediaImageComponent,
    GetEntityPipe,
    TranslocoModule,
    LoadingDirective,
  ],
  template: `
    <ng-container *ngrxLet="partnerLoaded$; let partnerLoaded">
      <ng-container *ngIf="partnerLoaded">
        <ng-container *ngrxLet="partner$; let partner">
          <ng-container *ngIf="partner">
            <i2l-media-image
              class="logo"
              [image]="partner.logo"
            ></i2l-media-image>
          </ng-container>
        </ng-container>
        <ng-container
          *i2lLoading="
            projectsLoading$ | ngrxPush;
            richError: projectsRichError$ | ngrxPush;
            classes: ['relative', 'min-h-screen-30']
          "
        ></ng-container>
        <ng-container
          *ngrxLet="hasEmptyProjectsResult$; let hasEmptyProjectsResult"
        >
          <ng-container *ngIf="hasEmptyProjectsResult">
            <ng-container *transloco="let t">
              <span
                [innerHTML]="t('partners.no-projects')"
                class="no-projects-message mt-3xl mb-5xl"
              ></span>
            </ng-container>
          </ng-container>
          <ng-container *ngIf="!hasEmptyProjectsResult">
            <msd-ui-grid-list
              class="project-list"
              [data]="projects$ | ngrxPush"
            ></msd-ui-grid-list>
          </ng-container>
        </ng-container>
        <!--<ng-container
          *i2lLoading="
            partnersLoading$ | ngrxPush;
            richError: partnersRichError$ | ngrxPush;
            classes: ['relative', 'min-h-screen-50']
          "
        ></ng-container>
        <ng-container *ngrxLet="partners$; let partners">
          <ng-container *ngIf="partners">
            <ng-container *ngIf="partners.length > 0">
              <ng-container *transloco="let t">
                <h3
                  [innerHTML]="t('partners.others')"
                  class="text-uppercase"
                ></h3>
              </ng-container>
              <msd-ui-grid-list
                class="partner-list"
                [data]="partners$ | ngrxPush"
              ></msd-ui-grid-list>
            </ng-container>
          </ng-container>
        </ng-container>-->
      </ng-container>
    </ng-container>
  `,
  styleUrls: ['./partner.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [EntityStore, EntitiesStore],
})
export class PartnerComponent
  extends ComponentStore<any>
  implements RoutedComponent
{
  readonly partner$ = this.partnerStore.entity$;
  readonly partnerLoaded$ = this.partnerStore.loaded$.pipe(delay(0));
  readonly projects$ = this.projectsStore.entities$;
  readonly hasEmptyProjectsResult$ = this.projectsStore.hasEmptyResult$;

  setEntityId = this.effect((entityId$: Observable<string>) => {
    return entityId$.pipe(
      tap((partnerId) => {
        this.partnerStore.reset();
        this.partnerStore.load({
          query: partnersDetailQuery,
          entityId: partnerId,
        });
        const partnerProjectsQuery: JsonApiQuery = {
          ...partnerProjectList,
          filters: { 'partners.id': partnerId },
        };
        this.projectsStore.load({
          query: partnerProjectsQuery,
          context: partnerId,
        });
        //this.partnersStore.load({ query: partnersListQuery });
      })
    );
  });

  // overallRichError$: Observable<RoutedComponentError|undefined> = this.partnerStore.richError$;
  overallLoading$: Observable<boolean> = this.partnerStore.loading$;

  readonly projectsLoading$ = this.projectsStore.notInitialSuccess$;
  readonly projectsRichError$: Observable<RoutedComponentError | undefined> =
    this.projectsStore.richError$;

  constructor(
    private partnerStore: EntityStore<NodePartner>,
    private projectsStore: EntitiesStore<UiProjectGridItem>,
    activateRoute: ActivatedRoute
  ) {
    super();
    this.projectsStore.modelClass = UiProjectGridItem;
    this.setEntityId(
      activateRoute.params.pipe(map((params) => params['partnerId']))
    );
  }
}
